import {
  TuiInputNumber,
  TuiComboBox,
  TuiSelect,
  TuiInputDate,
  TuiChevron,
} from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TUI_DEFAULT_MATCHER } from '@taiga-ui/cdk';
import {
  TuiDataList,
  TuiButton,
  TuiNumberFormat,
  TuiInput,
  TuiDropdown,
  TuiTextfield,
  TuiLabel,
  TuiCalendar,
} from '@taiga-ui/core';
import type {
  LogisticRequestExtraField,
  LogisticRequestType,
} from '@outkeep/shared';
import { TranslatePipe } from '@ngx-translate/core';
import type {
  Counterparty,
  CounterpartySubdivision,
  Product,
} from '@core/types';
import type {
  RequestCreateProductRow,
  RequestCreateProductSelection,
  RequestCreateRequestTypeOption,
  RequestCreateExtraFieldControlValue,
} from '../../request-create.types';

@Component({
  selector: 'x-request-create-step-1c',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TuiTextfield,
    TuiLabel,
    TuiChevron,
    TuiCalendar,
    ReactiveFormsModule,
    TuiButton,
    TuiComboBox,
    TuiDataList,
    TuiInputDate,
    TuiInput,
    TuiInputNumber,
    TuiSelect,
    TranslatePipe,
    TuiNumberFormat,
    TuiDropdown,
  ],
  templateUrl: './step-1c-managers-products.component.html',
})
export class RequestCreateStep1cComponent {
  readonly form = input.required<FormGroup>();
  readonly extraForm =
    input.required<
      FormGroup<
        Record<string, FormControl<RequestCreateExtraFieldControlValue>>
      >
    >();
  readonly requestTypeControl =
    input.required<FormControl<LogisticRequestType>>();
  readonly requestTypeOptions = input<RequestCreateRequestTypeOption[]>([]);
  readonly visibleExtraFields = input<LogisticRequestExtraField[]>([]);
  readonly availableProducts = input<Product[]>([]);
  readonly contractors = input<Counterparty[]>([]);
  readonly subdivisions = input<CounterpartySubdivision[]>([]);
  readonly selectedProducts = input<RequestCreateProductRow[]>([]);
  readonly addProduct = output<RequestCreateProductSelection>();
  readonly removeProduct = output<number>();

  readonly productIdControl = new FormControl<number | null>(null);
  readonly amountControl = new FormControl<number>(1, { nonNullable: true });
  readonly productSearch = signal('');

  readonly filteredProducts = computed(() => {
    const search = this.productSearch();

    return this.availableProducts().filter(product =>
      TUI_DEFAULT_MATCHER(product.name, search),
    );
  });

  readonly stringifyProduct = (id: number): string =>
    this.availableProducts().find(product => product.id === id)?.name ?? '';

  readonly stringifyRequestType = (value: LogisticRequestType): string =>
    this.requestTypeOptions().find(option => option.value === value)?.label ??
    value;

  readonly stringifyContractor = (id: number): string =>
    this.contractors().find(contractor => contractor.id === id)?.name ?? '';

  readonly stringifySubdivision = (id: number): string =>
    this.subdivisions().find(subdivision => subdivision.id === id)?.name ?? '';

  extraControl(
    name: string,
  ): FormControl<RequestCreateExtraFieldControlValue> | null {
    return this.extraForm().controls[name] ?? null;
  }

  emitAddProduct(): void {
    const productId = this.productIdControl.value;
    const amount = this.amountControl.value;

    if (!productId || amount < 1) {
      return;
    }

    this.addProduct.emit({
      productId,
      amount,
    });
    this.productIdControl.setValue(null);
    this.amountControl.setValue(1);
    this.productSearch.set('');
  }
}
